import React, { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Vehicle } from '../../types/vehicle';
import { useAuth } from '../../contexts/AuthContext';
import { collection, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

interface MileageRecorderProps {
  vehicle: Vehicle;
  onRecorded?: () => void;
}

const MileageRecorder: React.FC<MileageRecorderProps> = ({ vehicle, onRecorded }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [mileage, setMileage] = useState<number>(vehicle.mileage || 0);
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      // Km óra előzmény mentése - undefined értékek szűrése
      const recordData: any = {
        vehicleId: vehicle.id,
        mileage: parseInt(mileage.toString()),
        date: new Date(date),
        recordedBy: user.uid,
        createdAt: serverTimestamp(),
        organizationId: user.organizationId
      };

      // Csak akkor adjuk hozzá a notes-ot, ha van értéke
      if (notes && notes.trim()) {
        recordData.notes = notes.trim();
      }

      await addDoc(collection(db, 'mileageRecords'), recordData);

      // Jármű km óra frissítése
      await updateDoc(doc(db, 'vehicles', vehicle.id), {
        mileage: parseInt(mileage.toString()),
        updatedAt: serverTimestamp()
      });

      toast.success('Km óra rögzítve');
      setIsOpen(false);
      setNotes('');
      if (onRecorded) onRecorded();
    } catch (error: any) {
      toast.error(`Hiba: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
      >
        <PlusIcon className="h-4 w-4 mr-2" />
        Km óra rögzítése
      </button>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
        Új km óra állás rögzítése
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Km óra állás *
            </label>
            <input
              type="number"
              required
              min={vehicle.mileage || 0}
              value={mileage}
              onChange={(e) => setMileage(parseInt(e.target.value))}
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Jelenlegi: {vehicle.mileage.toLocaleString()} km
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Dátum *
            </label>
            <input
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Megjegyzés
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Pl. Tankolás, szerviz, stb..."
            className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              setMileage(vehicle.mileage || 0);
              setNotes('');
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {loading ? 'Mentés...' : 'Rögzítés'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MileageRecorder;
